import { useMemo } from 'react';
import { toast } from 'react-toastify';

import { useAddWalletMutation, useDeleteWalletMutation, useGetSIWENonceMutation } from '@query';

import { DeserializedUser, WALLET_NETWORK } from '@api';

import { ADD_WALLET_MESSAGE } from '@shared/constants';
import { copyToClipboardWithToast } from '@shared/helpers/copyToClipboardWithToast';
import displayErrorToast from '@shared/helpers/displayErrorToast';
import solanaAPI from '@shared/helpers/web3/solana';

import { getErrorMessage } from '@utilities/errors/getErrorMessage';

interface UseConnectSolanaToMagicIdProps {
  user?: DeserializedUser | null;
  isAuth: boolean;
}

export const useManageSolanaWallet = ({ user, isAuth }: UseConnectSolanaToMagicIdProps) => {
  const { isPhantomWalletInstalled, signMessage, getAddress } = solanaAPI;
  const { mutateAsync: deleteWallet, isPending: isDeletingSolanaWallet } =
    useDeleteWalletMutation();
  const { mutateAsync: getWalletNonce, isPending: isGettingNonce } = useGetSIWENonceMutation();
  const { mutateAsync: connectSolanaWallet, isPending: isConnectingSolanaWallet } =
    useAddWalletMutation();

  const solanaWallet = useMemo(() => {
    return user?.wallets?.find((wallet) => wallet.attributes.network === WALLET_NETWORK.SOLANA);
  }, [user]);

  const solanaAddress = solanaWallet?.attributes?.address;

  const getMessageToSign = (nonce: string) => {
    return `${ADD_WALLET_MESSAGE}\nNonce: ${nonce}`;
  };

  const fetchNonce = async () => {
    try {
      return await getWalletNonce();
    } catch (error) {
      toast.error(`Unable to retrieve Nonce. ${getErrorMessage(error)}`);
      throw error;
    }
  };

  const fetchSignature = async (nonce: string) => {
    try {
      return await signMessage(getMessageToSign(nonce));
    } catch (error) {
      toast.error(`Unable to retrieve Solana signature. ${getErrorMessage(error)}`);
      throw error;
    }
  };

  const connectToSolanaWallet = async (address: string, nonce: string, signature: string) => {
    try {
      await connectSolanaWallet({
        network: WALLET_NETWORK.SOLANA,
        wallet: 'Phantom',
        address,
        signature,
        message: getMessageToSign(nonce),
      });
    } catch (error) {
      toast.error(`Unable to connect Solana wallet. ${getErrorMessage(error)}`);
      throw error;
    }
  };

  const handleConnectButtonClick = async () => {
    if (!isPhantomWalletInstalled) {
      return toast.error('Phantom wallet is not installed');
    }

    try {
      const address = await getAddress();
      const nonce = await fetchNonce();
      const signature = await fetchSignature(nonce);

      if (!nonce || !signature) {
        return;
      }

      await connectToSolanaWallet(address, nonce, signature);
      toast.success('Solana wallet successfully connected');
    } catch (error) {
      toast.error(`Unable to connect Solana wallet. ${getErrorMessage(error)}`);
    }
  };

  const handleCopyClick = () => {
    if (solanaAddress) {
      copyToClipboardWithToast(solanaAddress);
    } else {
      displayErrorToast({ error: 'No Solana Wallet address available.' });
    }
  };

  const handleDeleteClick = async () => {
    try {
      const solanaWalletId = solanaWallet?.id;

      if (!solanaWalletId) {
        throw new Error('Wallet not defined');
      }

      await deleteWallet(solanaWalletId);

      toast.success('Wallet successfully unassigned from MagicID');
    } catch (error) {
      displayErrorToast({ error });
    }
  };

  const disabled = isConnectingSolanaWallet || isGettingNonce || !isAuth;

  return {
    disabled,
    handleConnectButtonClick,
    handleCopyClick,
    handleDeleteClick,
    isConnectingSolanaWallet: isGettingNonce || isConnectingSolanaWallet,
    isDeletingSolanaWallet,
    solanaAddress,
    solanaWallet,
  };
};
